/**
 * Hulk Options "Quantity <Finish>" fields — two fixes in one file:
 *
 * 1. LIVE TOTAL DISPLAY: sums all "Quantity <Finish>" fields (e.g.
 *    "Quantity Gold Finish", "Quantity Silver Finish") and writes the
 *    total into the visible quantity stepper next to Add to Cart, so it
 *    reflects what will actually be ordered as the customer types.
 *
 * 2. CART-ADD FIX: the "Add-to-Cart (No Personalization Needed)" button
 *    used to send the real quantity as inert text in a cart line
 *    property while the actual Shopify line quantity stayed at 1 —
 *    undercharging customers. This intercepts that specific button's
 *    click (capture phase, before EW's own handler runs) and adds the
 *    correct multi-item cart request instead. Confirmed working via live
 *    testing — the cart line now shows the real quantity entered.
 *
 * Does NOT touch "Personalize Now" / the EW wizard, which already
 * creates correct per-item cart lines on its own.
 */
(function () {
  "use strict";

  const QUANTITY_PROP_RE = /Quantity/i;
  const NO_PERSONALIZATION_TEXT_RE = /no\s+personalization/i;

  // ---------------------------------------------------------------------
  // Part 1: live total display
  // ---------------------------------------------------------------------
  function initLiveQuantityTotal(form) {
    if (form.dataset.hulkQtyTotalBound === "1") return; // already wired up

    const optionBlocks = Array.from(form.querySelectorAll(".hulkapps_option")).filter((block) => {
      const label = block.querySelector(".hulkapps_option_name");
      return (
        label &&
        label.getAttribute("aria-label") &&
        QUANTITY_PROP_RE.test(label.getAttribute("aria-label").trim())
      );
    });

    if (optionBlocks.length === 0) return false; // Hulk hasn't rendered its fields into this form yet

    const quantityInputs = optionBlocks
      .map((block) => block.querySelector(".hulkapps_option_value input[type='text']"))
      .filter(Boolean);

    if (quantityInputs.length === 0) return false;

    const mainQtyInput = form.querySelector('input[name="quantity"]');
    if (!mainQtyInput) return false;

    function recalcTotal() {
      const total = quantityInputs.reduce((sum, input) => {
        const val = parseInt(input.value, 10);
        return sum + (isNaN(val) || val < 0 ? 0 : val);
      }, 0);

      if (total > 0) {
        mainQtyInput.value = total;
        mainQtyInput.dispatchEvent(new Event("change", { bubbles: true }));
        mainQtyInput.dispatchEvent(new Event("input", { bubbles: true }));
      }
    }

    quantityInputs.forEach((input) => {
      input.addEventListener("input", recalcTotal);
      input.addEventListener("change", recalcTotal);
    });

    recalcTotal();
    form.dataset.hulkQtyTotalBound = "1";
    console.log("[Hulk quantity fix] Live total wired up for", quantityInputs.length, "field(s).");
    return true;
  }

  function tryInitAllForms() {
    const forms = document.querySelectorAll("form[action*='/cart/add']");
    let allBound = forms.length > 0;
    forms.forEach((form) => {
      if (form.dataset.hulkQtyTotalBound !== "1") {
        const bound = initLiveQuantityTotal(form);
        if (!bound) allBound = false;
      }
    });
    return allBound;
  }

  // Hulk Options renders its fields into the form asynchronously, some
  // time after the page (and this script) first loads — so a one-time
  // check at DOMContentLoaded finds nothing. Watch the DOM for Hulk's
  // fields to actually appear, then wire up the live total at that point.
  function watchForHulkFields() {
    if (tryInitAllForms()) return; // already found everything — no need to observe

    const observer = new MutationObserver(() => {
      if (tryInitAllForms()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Safety net in case MutationObserver somehow misses it
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      if (tryInitAllForms() || attempts > 40) {
        clearInterval(interval);
        observer.disconnect();
      }
    }, 250); // up to ~10 seconds
  }

  // ---------------------------------------------------------------------
  // Part 2: cart-add fix for the "No Personalization" button
  // ---------------------------------------------------------------------
  function extractProperties(flat) {
    const properties = {};
    Object.keys(flat).forEach((key) => {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match) properties[match[1]] = flat[key];
    });
    return properties;
  }

  function buildSplitItems(flat) {
    const variantId = parseInt(flat.id, 10);
    if (!variantId) return null;

    const properties = extractProperties(flat);
    const quantityKeys = Object.keys(properties).filter((k) => QUANTITY_PROP_RE.test(k));

    const matched = quantityKeys
      .map((k) => ({ key: k, value: parseInt(properties[k], 10) }))
      .filter((g) => !isNaN(g.value) && g.value > 0);

    if (matched.length === 0) return null;

    const otherProperties = {};
    Object.keys(properties).forEach((k) => {
      if (!QUANTITY_PROP_RE.test(k)) otherProperties[k] = properties[k];
    });

    return matched.map((g) => ({
      id: variantId,
      quantity: g.value,
      properties: Object.assign({}, otherProperties, { [g.key]: String(g.value) }),
    }));
  }

  function findRelevantForm(button) {
    const form = button.closest("form");
    if (form) return form;
    const formId = button.getAttribute("form");
    if (formId) return document.getElementById(formId);
    return null;
  }

  async function handleNoPersonalizationClick(evt, button) {
    const form = findRelevantForm(button);
    if (!form) return;

    const formData = new FormData(form);
    const flat = {};
    for (const [key, value] of formData.entries()) flat[key] = value;

    const items = buildSplitItems(flat);
    if (!items) return; // no Hulk quantity fields with values — let default behavior run

    evt.preventDefault();
    evt.stopImmediatePropagation();

    button.setAttribute("disabled", "disabled");

    try {
      console.log("[Hulk quantity fix] Adding", items.length, "line item(s):", items);

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
      const response = await fetch(`${root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.description || "Add to cart failed");
      }

      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer && typeof cartDrawer.refresh === "function") {
        await cartDrawer.refresh();
      }
      document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:open", { detail: { opener: button } }));
      document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:refresh"));
    } catch (err) {
      console.error("[Hulk quantity fix] Add to cart failed:", err);
      alert("Sorry, something went wrong adding this to your cart. Please try again.");
    } finally {
      button.removeAttribute("disabled");
    }
  }

  document.addEventListener(
    "click",
    function (evt) {
      const button = evt.target.closest("button, a, [role='button']");
      if (!button) return;
      if (!NO_PERSONALIZATION_TEXT_RE.test((button.textContent || "").trim())) return;
      handleNoPersonalizationClick(evt, button);
    },
    true
  );

  watchForHulkFields();

  console.log("✅ Hulk quantity fix installed.");
})();
